import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, XCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { verifyTransaction } from '../services/paymentService';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
import { triggerInvoiceReceiptEmail } from '../services/emailTriggerService';
export function PaymentCallbackPage() {
  const {
    user,
    refreshUser
  } = useAuth();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<'verifying' | 'success' | 'pending' | 'failed'>('verifying');
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 30; // 30 retries * 2 seconds = 1 minute max
  useEffect(() => {
    document.title = 'Payment Verification - Toxic Detector';
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference') || urlParams.get('trxref');
    if (!reference) {
      setError('No payment reference found in URL');
      setVerificationStatus('failed');
      return;
    }
    if (!user?.id) {
      setError('User not authenticated');
      setVerificationStatus('failed');
      return;
    }
    verifyPayment(reference);
  }, [user?.id]);
  const verifyPayment = async (reference: string) => {
    try {
      console.log('Verifying transaction with reference:', reference);
      const response = await verifyTransaction(reference);
      console.log('Verification response:', response);
      if (response.status === 'success') {
        // Payment successful
        setVerificationStatus('success');
        // Track payment completion
        await trackCriticalEvent('Payment_Completed', {
          plan_type: response.plan_type || 'unknown',
          amount_paid: response.amount || 0,
          transaction_reference: reference,
          payment_method: 'paystack',
          is_first_purchase: response.is_first_purchase || false
        });
        // Queue invoiceReceipt email via email_logs table
        try {
          const amountStr = response.amount ? `${(response.amount / 100).toFixed(2)}` : '$2.99';
          await triggerInvoiceReceiptEmail(`${window.location.origin}/payment-success`, `${window.location.origin}/dashboard`, amountStr, {
            invoiceNumber: reference,
            planName: response.plan_type || 'Single Scan'
          });
        } catch (emailErr) {
          console.error('[PaymentCallback] Failed to queue invoice email:', emailErr);
          // Non-blocking — don't fail the payment flow for an email issue
        }
        // Update local state
        await refreshUser();
        // Redirect to success page after brief delay
        setTimeout(() => {
          navigate('/payment-success');
        }, 1000);
      } else if (response.status === 'pending') {
        // Payment still pending
        setVerificationStatus('pending');
        // Retry after 2 seconds if under max retries
        if (retryCount < MAX_RETRIES) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            setVerificationStatus('verifying');
            verifyPayment(reference);
          }, 2000);
        } else {
          setError('Payment verification timed out. Please contact support if you were charged.');
          setVerificationStatus('failed');
          // Track timeout failure
          await trackCriticalEvent('Payment_Failed', {
            plan_type: 'unknown',
            failure_reason: 'verification_timeout',
            transaction_reference: reference
          });
        }
      } else {
        // Payment failed
        setError(response.message || 'Payment verification failed');
        setVerificationStatus('failed');
        // Track payment failure
        await trackCriticalEvent('Payment_Failed', {
          plan_type: response.plan_type || 'unknown',
          failure_reason: response.message || 'verification_failed',
          transaction_reference: reference
        });
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err.message || 'Failed to verify payment');
      setVerificationStatus('failed');
      // Track error
      await trackCriticalEvent('Payment_Failed', {
        plan_type: 'unknown',
        failure_reason: err.message || 'exception',
        transaction_reference: reference
      });
    }
  };
  // Verifying state
  if (verificationStatus === 'verifying') {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505]" data-id="element-9535">
        <WebsiteHeader data-id="element-9536" />

        <div className="max-w-2xl mx-auto w-full px-5 pt-32 pb-10" data-id="element-9537">
          <div className="bg-[#0a0a0a] rounded-2xl p-8 md:p-12 border border-[#FF0000]/30 shadow-[0_0_50px_rgba(255,0,0,0.1)]" data-id="element-9538">
            <div className="text-center" data-id="element-9539">
              <div className="mb-8 flex justify-center" data-id="element-9540">
                <div className="relative" data-id="element-9541">
                  <div className="absolute inset-0 border-4 border-[#FF0000]/20 rounded-full animate-ping" data-id="element-9542"></div>
                  <div className="relative w-24 h-24 border-4 border-[#FF0000] border-t-transparent rounded-full animate-spin" data-id="element-9543"></div>
                  <Loader2 size={48} className="absolute inset-0 m-auto text-[#FF0000] animate-pulse" data-id="element-9544" />
                </div>
              </div>

              <h1 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-9545">
                Verifying Transaction
              </h1>

              <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-9546">
                Confirming your payment with Paystack...
              </p>

              <div className="bg-[#FF0000]/5 border border-[#FF0000]/20 rounded-xl p-4" data-id="element-9547">
                <p className="text-[#FF0000] font-mono text-xs uppercase tracking-wider" data-id="element-9548">
                  ⚡ Processing • Do not close this window
                </p>
              </div>

              {/* Animated dots */}
              <div className="flex items-center justify-center space-x-2 mt-8" data-id="element-9549">
                <div className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" data-id="element-9550"></div>
                <div className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" style={{
                animationDelay: '0.2s'
              }} data-id="element-9551"></div>
                <div className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" style={{
                animationDelay: '0.4s'
              }} data-id="element-9552"></div>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // Pending state (waiting for payment to complete)
  if (verificationStatus === 'pending') {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505]" data-id="element-9553">
        <WebsiteHeader data-id="element-9554" />

        <div className="max-w-2xl mx-auto w-full px-5 pt-32 pb-10" data-id="element-9555">
          <div className="bg-[#0a0a0a] rounded-2xl p-8 md:p-12 border border-[#FFD700]/30 shadow-[0_0_50px_rgba(255,215,0,0.1)]" data-id="element-9556">
            <div className="text-center" data-id="element-9557">
              <div className="mb-8 flex justify-center" data-id="element-9558">
                <div className="bg-[#FFD700]/10 rounded-full p-6 border border-[#FFD700]/30" data-id="element-9559">
                  <Loader2 size={64} className="text-[#FFD700] animate-spin" data-id="element-9560" />
                </div>
              </div>

              <h1 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-9561">
                Payment Pending
              </h1>

              <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-9562">
                Your payment is being processed. This usually takes a few
                seconds...
              </p>

              <div className="bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-xl p-4 mb-6" data-id="element-9563">
                <p className="text-[#FFD700] font-mono text-xs uppercase tracking-wider mb-2" data-id="element-9564">
                  Retry {retryCount + 1} of {MAX_RETRIES}
                </p>
                <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden" data-id="element-9565">
                  <div className="bg-[#FFD700] h-full transition-all duration-300" style={{
                  width: `${(retryCount + 1) / MAX_RETRIES * 100}%`
                }} data-id="element-9566"></div>
                </div>
              </div>

              <p className="text-gray-500 font-mono text-xs" data-id="element-9567">
                Checking payment status every 2 seconds...
              </p>
            </div>
          </div>
        </div>
      </div>;
  }
  // Failed state
  if (verificationStatus === 'failed') {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505]" data-id="element-9568">
        <WebsiteHeader data-id="element-9569" />

        <div className="max-w-2xl mx-auto w-full px-5 pt-32 pb-10" data-id="element-9570">
          <div className="bg-[#0a0a0a] rounded-2xl p-8 md:p-12 border border-red-500/30 shadow-[0_0_50px_rgba(239,68,68,0.1)]" data-id="element-9571">
            <div className="text-center" data-id="element-9572">
              <div className="mb-8 flex justify-center" data-id="element-9573">
                <div className="bg-red-500/10 rounded-full p-6 border border-red-500/30" data-id="element-9574">
                  {error?.includes('timed out') ? <AlertTriangle size={64} className="text-yellow-500" data-id="element-9575" /> : <XCircle size={64} className="text-red-500" data-id="element-9576" />}
                </div>
              </div>

              <h1 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-9577">
                {error?.includes('timed out') ? 'Verification Timeout' : 'Verification Failed'}
              </h1>

              <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-9578">
                {error || 'Unable to verify your payment'}
              </p>

              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-8 text-left" data-id="element-9579">
                <p className="text-blue-400 font-mono text-xs uppercase tracking-wider mb-3" data-id="element-9580">
                  What to do next:
                </p>
                <ul className="text-gray-300 text-sm space-y-2 font-mono" data-id="element-9581">
                  <li data-id="element-9582">• Check your email for payment confirmation</li>
                  <li data-id="element-9583">• Refresh this page in a few minutes</li>
                  <li data-id="element-9584">• Contact support if payment was deducted</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-9585">
                <button onClick={() => window.location.reload()} className="flex-1 bg-[#FF0000] text-white px-8 py-4 rounded-xl font-bold uppercase tracking-wider hover:bg-red-600 transition-colors" data-id="element-9586">
                  Refresh Page
                </button>
                <Link to="/dashboard" className="flex-1 bg-white/10 border border-gray-700 text-white px-8 py-4 rounded-xl font-bold uppercase tracking-wider hover:bg-white/20 transition-colors text-center" data-id="element-9587">
                  Go to Dashboard
                </Link>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-800" data-id="element-9588">
                <p className="text-gray-500 font-mono text-xs" data-id="element-9589">
                  Need help?{' '}
                  <a href="mailto:support@toxicdetector.com" className="text-[#FF0000] hover:underline" data-id="element-9590">
                    Contact support
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // Success state (brief, before redirect)
  return <div className="flex flex-col w-full min-h-screen bg-[#050505]" data-id="element-9591">
      <WebsiteHeader data-id="element-9592" />

      <div className="max-w-2xl mx-auto w-full px-5 pt-32 pb-10" data-id="element-9593">
        <div className="bg-[#0a0a0a] rounded-2xl p-8 md:p-12 border border-green-500/30 shadow-[0_0_50px_rgba(34,197,94,0.1)]" data-id="element-9594">
          <div className="text-center" data-id="element-9595">
            <div className="mb-8 flex justify-center" data-id="element-9596">
              <div className="bg-green-500/10 rounded-full p-6 border border-green-500/30 animate-pulse" data-id="element-9597">
                <svg className="w-16 h-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-9598">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-9599" />
                </svg>
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-9600">
              Payment Verified
            </h1>

            <p className="text-gray-400 font-mono text-sm" data-id="element-9601">
              Redirecting to success page...
            </p>
          </div>
        </div>
      </div>
    </div>;
}